import { useState, useMemo } from 'react';
import { Box, CircularProgress, Popover, Typography } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import type { ColumnMetadata, LookupDataItem } from '../../types';
import { useGetLookupQuery } from '../../api/gridApi';
import { parseRelatedColumns } from '../../utils';
import { LookupGridPanel } from './LookupGridPanel';
import { CellTextWithTooltip } from './cells/CellTextWithTooltip';

type LookupGridDropdownProps = {
  value: unknown;
  lookupMap: Record<string, string>;
  columnMetadata: ColumnMetadata;
  nodeKey?: string;
  fieldKey?: string;
  baseFilter?: unknown;
  parentId?: string;
  selectedEntity?: string;
  staticData?: LookupDataItem[];
  onChange: (id: string) => void;
  disabled?: boolean;
};

export const LookupGridDropdown = ({
  value,
  lookupMap,
  columnMetadata,
  nodeKey,
  fieldKey,
  baseFilter,
  parentId,
  selectedEntity,
  staticData,
  onChange,
  disabled = false,
}: LookupGridDropdownProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const columns = useMemo(
    () => parseRelatedColumns(columnMetadata.additionalData),
    [columnMetadata.additionalData]
  );

  const gridId = columnMetadata.additionalData?.gridId || '';
  const usePaging = columnMetadata.additionalData?.usePaging ?? false;
  const lookupWidth = columnMetadata.additionalData?.lookupWidth ?? 400;
  const shouldUseLookupQuery = !baseFilter && open && !!nodeKey && !!fieldKey;

  const lookupQuery = useGetLookupQuery(
    {
      nodeKey: nodeKey || '',
      fieldKey: fieldKey || '',
      forDropdown: true,
      defaultFilters: columnMetadata.defaultFilters,
      parentId: parentId,
      selectedEntity,
    },
    {
      skip: !shouldUseLookupQuery,
    }
  );

  const lookupStaticData = lookupQuery.data?.array;
  const hasLookupStaticData = Array.isArray(lookupStaticData);

  const displayValue = lookupMap[String(value ?? '')] || '';

  return (
    <>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={columnMetadata.title || columnMetadata.caption || 'Select value'}
        onClick={(e) => !disabled && setAnchorEl(e.currentTarget)}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setAnchorEl(e.currentTarget);
          }
          if (e.key === 'Escape' && open) {
            setAnchorEl(null);
          }
        }}
        sx={{
          cursor: disabled ? 'default' : 'pointer',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          minWidth: 0,
          overflow: 'hidden',
          px: 0.5,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <CellTextWithTooltip rawText={displayValue || '-'}>
            <span
              style={{
                opacity: disabled ? 0.38 : 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
              }}
            >
              {displayValue || '-'}
            </span>
          </CellTextWithTooltip>
        </Box>
        <ArrowDropDown
          sx={{
            fontSize: 20,
            flexShrink: 0,
            color: disabled ? 'action.disabled' : 'action.active',
          }}
        />
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableScrollLock
        PaperProps={{ sx: { width: lookupWidth, maxHeight: 450 } }}
      >
        {open && shouldUseLookupQuery && lookupQuery.isLoading && !hasLookupStaticData ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : open && shouldUseLookupQuery && lookupQuery.isError ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Failed to load lookup options.
            </Typography>
          </Box>
        ) : (
          open && (
            <LookupGridPanel
              gridId={gridId}
              columns={columns}
              usePaging={shouldUseLookupQuery ? false : usePaging}
              baseFilter={shouldUseLookupQuery ? undefined : baseFilter}
              staticData={shouldUseLookupQuery ? lookupStaticData : staticData}
              onSelect={(id) => {
                onChange(id);
                setAnchorEl(null);
              }}
            />
          )
        )}
      </Popover>
    </>
  );
};
